import React from 'react';

import { useIntl } from 'react-intl';
import { Pie, ResponsiveContainer, PieChart as PieChartComp, Sector } from 'recharts';
import colors from 'tailwindcss/colors';

import { CombinedStatisticsKey, VisitStatisticTotalsKey } from 'src/features/statistic/types';
import { allColorCategoryMapping, statisticsTypeMapping } from 'src/features/statistic/utils/constants';
import { SectionBlockSubtitle } from 'src/registry/components/blocks/SectionBlock';
import { Skeleton } from 'src/registry/ui/Skeleton';

type Props = {
    dataKey: string;
    data?: { [key: string]: string | number }[];
    children?: React.ReactNode;
    isLoading: boolean;
    title: string;
    value?: number;
};

export default function PieChart({ dataKey, data, title, value, isLoading }: Props) {
    const { formatMessage } = useIntl();
    const [index, setIndex] = React.useState<null | number>(null);

    const fallbackData = [{ value: 1 }];
    const hasData = data && data.length > 0;

    return (
        <div className="relative flex h-full w-full items-center justify-center">
            <ResponsiveContainer className="absolute z-10 aspect-square min-h-50" width="100%" height="100%">
                <PieChartComp>
                    <Pie
                        data={hasData ? data : fallbackData}
                        dataKey={dataKey}
                        cx="50%"
                        cy="50%"
                        innerRadius="70%"
                        outerRadius="100%"
                        isAnimationActive
                        shape={(props) => {
                            const isEmpty = !hasData;
                            return (
                                <Sector
                                    {...props}
                                    className="hover:opacity-75"
                                    fill={
                                        isEmpty
                                            ? colors.gray[100]
                                            : allColorCategoryMapping[
                                                  data?.[props.index]?.title as CombinedStatisticsKey
                                              ]
                                    }
                                    onMouseEnter={() => setIndex(!isEmpty ? props.index : null)}
                                    onMouseOut={() => setIndex(null)}
                                />
                            );
                        }}
                    />
                </PieChartComp>
            </ResponsiveContainer>
            <div className="absolute top-1/2 left-1/2 z-20 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center">
                <div className="flex flex-col items-center gap-2">
                    {isLoading ? (
                        <Skeleton className="h-8 w-27" />
                    ) : (
                        <p className="text-sm">
                            <span className="text-2xl font-semibold">
                                {index !== null && data ? data[index].value : value}
                            </span>
                        </p>
                    )}
                    <SectionBlockSubtitle className="text-center text-xs font-medium xl:text-sm">
                        {data && index !== null
                            ? formatMessage(statisticsTypeMapping[data[index]?.title as VisitStatisticTotalsKey])
                            : title}
                    </SectionBlockSubtitle>
                </div>
            </div>
        </div>
    );
}
